/**
 * Formatting helpers for the redesigned surfaces.
 *
 * These are intentionally separate from the inline `formatCurrency`
 * helpers that exist throughout the legacy components (SOVTab,
 * ChangeOrdersTab, etc.) — those return full-precision values for
 * tables and forms, while these compact forms are for KPI tiles and
 * project cards where space is tight and a million-dollar value
 * shouldn't eat half the card.
 */

/**
 * Full-precision currency, e.g. $11,690,552.00. Mirrors the legacy
 * formatCurrency() output including trailing cents — Duncan prefers the
 * detail everywhere a full dollar figure is shown. (Compact tiles that
 * can't fit cents use formatMoneyCompact below.)
 */
export const formatMoney = (n) => {
  const num = Number(n) || 0;
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Whole dollars: $19,726,152. For headline figures — the project strip and the Job
 *  Costs tiles (Duncan 2026-10-04) — where cents are noise; tables keep formatMoney. */
export const formatMoneyWhole = (n) => {
  const num = Math.round(Number(n) || 0) || 0; // `|| 0` turns a rounded -0 into 0
  return '$' + num.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

/**
 * Compact currency: $11.69M, $412k, $0. Picks decimals so values
 * always read with similar visual weight ($11.69M not $12M, $1.5M not
 * $1.50M).
 *
 * Thresholds:
 *   ≥ 1,000,000 → "$X.XXM" (2 dp for sub-$10M, 1 dp at ≥$10M)
 *   ≥ 1,000     → "$Xk"   (no decimals — k-scale doesn't need them)
 *   < 1,000     → "$N"    (whole)
 *   exactly 0   → "$0"    (drop the .00)
 */
export const formatMoneyCompact = (n) => {
  const num = Number(n) || 0;
  if (num === 0) return '$0';
  const abs = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  if (abs >= 1_000_000) {
    const m = abs / 1_000_000;
    const dp = m >= 10 ? 1 : 2;
    return `${sign}$${m.toFixed(dp)}M`;
  }
  if (abs >= 1_000) {
    return `${sign}$${Math.round(abs / 1_000)}k`;
  }
  return `${sign}$${Math.round(abs)}`;
};

/**
 * Percent with controlled decimals, defaulting to 1 dp.
 * formatPercent(46.0345) → "46.0%"
 */
export const formatPercent = (n, decimals = 1) => {
  const num = Number(n) || 0;
  return num.toFixed(decimals) + '%';
};
